import {
  fetchStaticCatalogProducts,
  getStaticCatalogSnapshotSync,
  subscribeCatalogBootstrap,
} from '@/utils/staticCatalog';

/** Ürün adı → URL segmenti (`Baby Diaper` → `baby-diaper`). */
export function slugifyProductName(name) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * @param {Array<{ id: string, name?: string }>} products
 * @returns {{ slugToId: Map<string, string>, idToSlug: Map<string, string> }}
 */
export function buildProductSlugIndex(products) {
  const byBase = new Map();
  for (const p of products || []) {
    if (!p?.id) continue;
    const base = slugifyProductName(p.name) || 'product';
    if (!byBase.has(base)) byBase.set(base, []);
    byBase.get(base).push(p);
  }

  const slugToId = new Map();
  const idToSlug = new Map();

  for (const list of byBase.values()) {
    list.sort((a, b) => String(a.id).localeCompare(String(b.id)));
    const base = slugifyProductName(list[0]?.name) || 'product';
    list.forEach((p, i) => {
      const slug = list.length === 1 ? base : i === 0 ? base : `${base}-${i + 1}`;
      slugToId.set(slug, p.id);
      idToSlug.set(p.id, slug);
    });
  }

  return { slugToId, idToSlug };
}

let cachedIndex = null;

function setIndexFromProducts(products) {
  if (!Array.isArray(products) || !products.length) return null;
  cachedIndex = buildProductSlugIndex(products);
  return cachedIndex;
}

export function getProductSlugIndex() {
  return cachedIndex;
}

/** Bellek / index bootstrap — senkron rota çözümü. */
export function ensureProductSlugIndexSync() {
  if (cachedIndex) return cachedIndex;
  const snap = getStaticCatalogSnapshotSync();
  if (snap?.length) return setIndexFromProducts(snap);
  return null;
}

/** Katalog yüklendikten sonra slug indeksi. */
export async function ensureProductSlugIndex() {
  if (cachedIndex) return cachedIndex;
  const snap = getStaticCatalogSnapshotSync();
  if (snap?.length) return setIndexFromProducts(snap);
  const products = await fetchStaticCatalogProducts();
  return setIndexFromProducts(products);
}

if (typeof window !== 'undefined') {
  subscribeCatalogBootstrap(() => {
    const snap = getStaticCatalogSnapshotSync();
    if (snap?.length) setIndexFromProducts(snap);
  });
}

/** `/products/baby-diaper` veya legacy `/products/wc-18628`. */
export function resolveProductIdFromUrlSegment(segment) {
  const seg = String(segment || '').trim();
  if (!seg) return null;
  try {
    const decoded = decodeURIComponent(seg);
    if (/^wc-\d+$/i.test(decoded)) return decoded;
    const idx = ensureProductSlugIndexSync();
    return idx?.slugToId.get(decoded) || null;
  } catch {
    return null;
  }
}

export function productSlugForIdSync(productId) {
  const id = String(productId || '').trim();
  if (!id) return null;
  const idx = ensureProductSlugIndexSync();
  return idx?.idToSlug.get(id) || null;
}

export async function productSlugForId(productId) {
  const id = String(productId || '').trim();
  if (!id) return null;
  const idx = await ensureProductSlugIndex();
  return idx?.idToSlug.get(id) || null;
}

export function productPathForIdSync(productId) {
  const slug = productSlugForIdSync(productId);
  if (slug) return `/products/${encodeURIComponent(slug)}`;
  return null;
}

export async function productPathForId(productId) {
  const slug = await productSlugForId(productId);
  if (slug) return `/products/${encodeURIComponent(slug)}`;
  return `/products/${encodeURIComponent(String(productId))}`;
}

export function isLegacyWcProductPathSegment(segment) {
  return /^wc-\d+$/i.test(String(segment || '').trim());
}
